import { useEffect, useState, useRef } from 'react';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { initIslandGame } from "../../scripts/islandMain";
import { db, functions } from '@/config/firebase';
import { httpsCallable } from 'firebase/functions';
import { useUser } from '@/context/UserContext';
import { useParams, useNavigate } from "react-router-dom";
import { GameSidebar } from '@/components/GameSidebar';
import MobileControls from './MobileControls';
import { blockToColour } from '../../scripts/blocks';
import { Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { useToast } from '@/context/ToastContext';
import { getIslandById, FlyingIsland } from '@/config/flyingIslands';
import { isLandscapeViewport, requestFullscreenLandscape, requestNativeOrientation } from '@/utils/orientation';
import { isMobileDevice } from '../../scripts/device';
import "@/styles/IslandGame.css";

const claimDailyIslandRewardCallable = httpsCallable(functions, 'claimDailyIslandReward');
const DAILY_ISLAND_REWARD_AMOUNT = 500;
const getCurrentDateString = () => new Date().toISOString().split('T')[0];

function IslandGame() {
    const { user } = useUser();
    const { islandId } = useParams<{ islandId: string }>();
    const wallet = useTonWallet();
    const [tonConnectUI] = useTonConnectUI();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const mountRef = useRef<HTMLDivElement>(null);
    const resizeRef = useRef<(() => void) | null>(null);
    const [loading, setLoading] = useState(true);
    const [islandData, setIslandData] = useState<FlyingIsland | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [player, setPlayer] = useState<any>(null);

    // Landscape State
    // State for daily island reward claim
    const [canClaimIslandReward, setCanClaimIslandReward] = useState(false);
    const [isClaimingIslandReward, setIsClaimingIslandReward] = useState(false);
    const [islandClaimMessage, setIsIslandClaimMessage] = useState<string | null>(null);
    const [showRewardModal, setShowRewardModal] = useState(false);
    // Landscape State
    const [isLandscape, setIsLandscape] = useState(() => isLandscapeViewport());
    const [useFallbackLandscape, setUseFallbackLandscape] = useState(false);

    const cleanupRef = useRef<(() => void) | null>(null);

    const handleConnectClick = () => {
        tonConnectUI.openModal();
    };

    const returnToMainWorld = () => {
        navigate('/game');
    };

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(isMobileDevice());
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!isMobile) return;
        requestFullscreenLandscape();
        setIsLandscape(true);
    }, [isMobile]);

    useEffect(() => {
        const syncOrientationState = () => {
            if (!useFallbackLandscape) {
                setIsLandscape(isLandscapeViewport());
            }
        };

        syncOrientationState();
        window.addEventListener('resize', syncOrientationState);
        window.addEventListener('orientationchange', syncOrientationState);
        window.screen?.orientation?.addEventListener?.('change', syncOrientationState);

        return () => {
            window.removeEventListener('resize', syncOrientationState);
            window.removeEventListener('orientationchange', syncOrientationState);
            window.screen?.orientation?.removeEventListener?.('change', syncOrientationState);
        };
    }, [useFallbackLandscape]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (resizeRef.current) {
                resizeRef.current();
            }
        }, 60);

        return () => window.clearTimeout(timer);
    }, [useFallbackLandscape, isLandscape]);

    // Effect to initialize the island game and fetch island data
    useEffect(() => {
        let isActive = true;
        let unsubscribeIslandData: () => void = () => { };

        const initialize = async (userData: any, data: FlyingIsland) => {
            try {
                if (!mountRef.current) {
                    console.error("Island Mount Ref is null!");
                    return;
                }

                const result = await initIslandGame(userData, data);
                if (isActive) {
                    cleanupRef.current = result.cleanup;
                    resizeRef.current = result.resize || null;
                    setPlayer(result.player);
                    setTimeout(() => {
                        if (isActive) setLoading(false);
                    }, 300);
                } else {
                    if (result.cleanup) result.cleanup();
                }
            } catch (error) {
                console.error("Island game initialization failed:", error);
                if (isActive) {
                    navigate('/game');
                }
            }
        };

        if (!user || !islandId) {
            if (!user) {
                navigate('/game');
                return;
            }
        }

        if (islandId && user) {
            setLoading(true);
            setIsIslandClaimMessage(null);

            const islandDocRef = doc(db, 'flyingIslands', islandId);
            unsubscribeIslandData = onSnapshot(islandDocRef, (docSnap) => {
                if (isActive) {
                    if (docSnap.exists()) {
                        const data = docSnap.data() as FlyingIsland;
                        if (user.uid !== data.owner) {
                            console.error(`User ${user.uid} is not the owner of island ${islandId}.`);
                            navigate("/game");
                            return;
                        }

                        setIslandData({ ...data, id: islandId } as FlyingIsland);
                    } else {
                        console.error(`Island ${islandId} not found or deleted.`);
                        navigate("/game");
                    }
                }
            }, (error) => {
                console.error(`Error fetching real-time island data for ${islandId}:`, error);
                if (isActive) navigate("/game");
            });

            // Initial fetch and game initialization using getIslandById
            getIslandById(islandId)
                .then((data) => {
                    if (!data) {
                        return;
                    }
                    if (user?.uid !== data?.owner) {
                        return;
                    }

                    if (isActive) {
                        initialize(user, { ...data, id: islandId } as FlyingIsland);
                    }
                })
                .catch((error: Error) => {
                    console.error("Initial get island data failed!", error)
                    if (isActive) navigate('/game');
                });
        }

        return () => {
            isActive = false;
            console.log('[IslandGame] Cleaning up island world...');
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
            unsubscribeIslandData();
        };
    }, [user, islandId, navigate]);

    // Update controls enabled state when menu opens/closes
    useEffect(() => {
        if (player && typeof player.setControlsEnabled === 'function') {
            player.setControlsEnabled(!isMenuOpen);
        }
        if (!isMenuOpen && resizeRef.current) {
            resizeRef.current();
        }
    }, [isMenuOpen, player]);

    // Effect to determine claim eligibility
    useEffect(() => {
        const todayString = getCurrentDateString();

        if (islandData && user?.uid === islandData.owner) {
            const lastClaimTimestamp = islandData.lastIslandRewardClaimTimestamp;

            let claimedToday = false;
            if (lastClaimTimestamp instanceof Timestamp) {
                const lastClaimDateString = new Date(lastClaimTimestamp.toDate()).toISOString().split('T')[0];
                if (lastClaimDateString === todayString) {
                    claimedToday = true;
                }
            }
            setCanClaimIslandReward(!claimedToday);
        } else {
            setCanClaimIslandReward(false);
        }

        if (!islandClaimMessage?.includes('Claimed')) {
            setIsIslandClaimMessage(null);
        }
    }, [islandData, user?.uid]);

    const handleClaimIslandReward = async () => {
        if (!user?.uid || !islandId) return;

        setIsClaimingIslandReward(true);
        setIsIslandClaimMessage(null);

        try {
            const result = await claimDailyIslandRewardCallable({ islandId });
            const data = result.data as any;
            if (data.success) {
                setIsIslandClaimMessage(`Claimed ${Number(data.claimedAmount).toFixed(4)} Bananium for your island!`);
                showToast(`Claimed ${Number(data.claimedAmount).toFixed(4)} Bananium!`, 'success');
            } else {
                setIsIslandClaimMessage(data.message || "Failed to claim.");
                showToast(data.message || "Failed to claim.", 'error');
            }
        } catch (error: any) {
            setIsIslandClaimMessage(error.message || "Failed to claim island reward.");
            showToast(error.message || "Failed to claim.", 'error');
        } finally {
            setIsClaimingIslandReward(false);
        }
    };

    const toggleOrientation = async () => {
        const targetLandscape = !(useFallbackLandscape || isLandscape);
        const nativeApplied = await requestNativeOrientation(targetLandscape);
        setUseFallbackLandscape(nativeApplied ? false : targetLandscape);
        setIsLandscape(targetLandscape);
    };

    return (
        <div className={`island-game-container ${useFallbackLandscape ? 'force-landscape' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
            {loading && (
                <div id="island-loading" className={loading ? 'visible' : 'hidden'}>
                    <div className="island-loading-copy">
                        <p className="island-loading-label">Sky Route</p>
                        <h1>{`Entering Island ${islandId?.slice(0, 8)}`}</h1>
                        <p className="island-loading-subtitle">Preparing biomes, structures, and world sync</p>
                    </div>
                    <div className="island-portal-container">
                        <div className="island-portal-ring">
                            <div className="island-portal-core"></div>
                            <div className="island-portal-particles">
                                <div className="island-portal-particle"></div>
                                <div className="island-portal-particle"></div>
                                <div className="island-portal-particle"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div ref={mountRef} id="island-game" className="three-container" />

            {isMobile && player && !isMenuOpen && (
                <MobileControls player={player} onOpenMenu={() => setIsMenuOpen(true)} />
            )}

            {(isMenuOpen || !isMobile) && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: isMenuOpen ? 'auto' : 'none', zIndex: 2000 }}>
                    {isMenuOpen && <GameSidebar onClose={() => setIsMenuOpen(false)} player={player} />}
                </div>
            )}

            <div id="toolbar-container">
                <div id="toolbar">
                    {Object.entries(blockToColour).map(([name, color], index) => (
                        <div
                            key={index + 1}
                            id={`toolbar-${index + 1}`}
                            className={`toolbar-icon`}
                            title={name}
                            style={{
                                backgroundColor: color,
                                width: '32px',
                                height: '32px',
                                margin: '4px',
                                borderRadius: '4px',
                                boxSizing: 'border-box'
                            }}
                        />
                    ))}
                    <img
                        className="toolbar-icon selected"
                        id="toolbar-0"
                        src="/textures/pickaxe.png"
                        alt="pickaxe"
                    />
                </div>
            </div>

            <div id="overlay">
                <div className="top-left-controls">
                    {islandData?.owner === user?.uid && islandId && (
                        <button
                            onClick={() => setShowRewardModal(!showRewardModal)}
                            className="reward-toggle-button island-control-btn"
                            title="Daily Reward"
                        >
                            🎁
                        </button>
                    )}

                    <button className='island-control-btn' onClick={returnToMainWorld} title="Back to Main World">
                        🔙
                    </button>
                    <button className='island-control-btn' onClick={toggleOrientation} title={isLandscape ? "Vertical Mode" : "Landscape Mode"}>
                        {isLandscape ? "📱" : "🔄"}
                    </button>
                </div>

                {/* Daily Island Reward Card - Modernized (Toggle-able) */}
                {showRewardModal && islandData?.owner === user?.uid && islandId && (
                    <div className="island-reward-card">
                        <button
                            onClick={() => setShowRewardModal(false)}
                            className="close-modal-button"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                        <div className="reward-icon">🏝️</div>
                        <h3>Daily Island Reward</h3>
                        <div className="reward-amount">
                            <span className="amount-value">{DAILY_ISLAND_REWARD_AMOUNT}</span>
                            <span className="amount-label">Bananium</span>
                        </div>

                        {canClaimIslandReward && !isClaimingIslandReward && (
                            <div className="claim-status available">
                                <span className="status-icon">✨</span>
                                <span>Ready to claim!</span>
                            </div>
                        )}

                        <button
                            onClick={handleClaimIslandReward}
                            disabled={isClaimingIslandReward || !user?.uid || !canClaimIslandReward}
                            className={`claim-button ${canClaimIslandReward && !isClaimingIslandReward ? 'pulse' : ''}`}
                        >
                            {canClaimIslandReward ? (isClaimingIslandReward ? '⏳ Claiming...' : '🎁 Claim Now') : "✓ Claimed"}
                        </button>

                        {islandClaimMessage && (
                            <div className={`claim-message ${islandClaimMessage.includes('Claimed') ? 'success' : 'error'}`}>
                                {islandClaimMessage}
                            </div>
                        )}

                        {!canClaimIslandReward && !isClaimingIslandReward && !islandClaimMessage?.includes('Claimed') && (
                            <p className="next-claim-info">Next claim available tomorrow</p>
                        )}
                    </div>
                )}
            </div>

            <div className="ui-overlay">
                {!isMobile && <div className="crosshair">+</div>}
            </div>

            {/* <div id="status"></div>
            <div id="info"></div>
            <div id="info-player-position" className="position-overlay" />

            <div className='wallet-connection'>
                <div className={wallet ? 'wallet-status-indicator connected' : 'wallet-status-indicator not-connected'}>
                    <button
                        onClick={handleConnectClick}
                        title='Wallet Status'
                        className='wallet-connect-button'
                    >
                        <span className="sr-only">{wallet ? 'Wallet Connected' : 'Wallet Disconnected'}</span>
                    </button>
                </div>
            </div> */}
        </div>
    );
}

export default IslandGame;
